import { Formik } from 'formik';
import { Button, Col, Modal, Row } from 'antd';
import { Form, Input } from 'formik-antd';
import React, { useEffect, useRef, useState } from 'react';
import { toNumber } from 'lodash';

import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { CompanyType } from 'features/company/CompanyDetailsPage/types/CompanyType';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  ContractorCompanyUpdateOptions,
  getContractorCompanyInfoFile,
  updateContractorCompany,
} from 'redux/reducers/contractorCompanySlice';
import SubmitButton from 'components/formik/SubmitButton';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import FormItem from 'components/formik/FormItem';
import { ContractorCompanyTypeEnum } from 'api/backofficeApiClient';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import CompanyStateSection from './CompanyStateSection';
import InternationalCompanyStateSection from './InternationalCompanyStateSection';
import {
  getAllCities,
  getAllCountries,
  getAllCountryCities,
  getAllStates,
} from 'redux/reducers/regionSlice';
import CompanyFileUpload from './CompanyFileUpload';
import { BaseUploadFile } from 'components/wrappers/BaseFileUpload';
import { generateUploadFileList } from 'utils/fileUtils';
import InputNumber from 'components/formik/InputNumber';
import { getSubmitValidationSchema } from './validation';

export interface CompanyFormValues {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country?: number;
  internationalCityName?: string;
  internationalStateName?: string;
  state?: number;
  city: string;
  address: string;
  zip: string;
  taxId?: number;
  workerFileKey: string[];
}

type Props = {
  companyDetails: CompanyType;
  onClose: () => void;
  onSuccess: () => void;
};
export default function CompanyModal(props: Props) {
  const { companyDetails, onClose, onSuccess } = props;
  const [contractorCompanyFileKeyInfo, setContractorCompanyFileKeyInfo] =
    useState<BaseUploadFile[]>([]);
  const isAdmin = useAppSelector(isAdminSelector);
  const dispatch = useAppDispatch();
  const isParentSC =
    companyDetails.contractorCompanyType === ContractorCompanyTypeEnum.ParentSC;

  const formRef: any = useRef();

  const loadContractorCompanyFile = async (companyId?: string) => {
    if (!companyId || !isParentSC) return;

    const result = await dispatch(
      getContractorCompanyInfoFile({
        companyId,
        isAdminRole: isAdmin as boolean,
      }),
    );

    if (getContractorCompanyInfoFile.fulfilled.match(result)) {
      setContractorCompanyFileKeyInfo(generateUploadFileList(result.payload));
    }
  };
  useEffect(() => {
    loadContractorCompanyFile(companyDetails?.id);
    // eslint-disable-next-line
  }, [companyDetails?.id]);

  useEffect(() => {
    dispatch(getAllCountries());
    dispatch(getAllCountryCities());
    dispatch(getAllStates());
    dispatch(getAllCities());
  }, [dispatch]);

  const initialValues: CompanyFormValues = {
    companyName: companyDetails.companyName || '',
    firstName: companyDetails.representativeContact?.firstName || '',
    lastName: companyDetails.representativeContact?.lastName || '',
    email: companyDetails.representativeContact?.email || '',
    phone: companyDetails.representativeContact?.phoneNumber || '',
    taxId: companyDetails.representativeContact?.taxId || undefined,
    country: companyDetails.location?.countryId,
    internationalCityName:
      companyDetails.location?.internationalCityName || undefined,
    internationalStateName:
      companyDetails.location?.internationalStateName || undefined,
    state: companyDetails.location?.stateId || undefined,
    city: companyDetails.location?.city || '',
    address: companyDetails.location?.address || '',
    zip: companyDetails.location?.zipCode || '',
    workerFileKey: companyDetails.representativeContact?.workerFileKey
      ? [companyDetails.representativeContact?.workerFileKey]
      : [],
  };

  const handleSubmit = async (data: CompanyFormValues) => {
    const values: ContractorCompanyUpdateOptions = {
      isAdmin: isAdmin,
      contractorCompanyId: companyDetails.id as string,
      data: {
        companyName: data.companyName || undefined,
        representativeContact: {
          firstName: data.firstName || undefined,
          lastName: data.lastName || undefined,
          email: data.email || undefined,
          phoneNumber: data.phone || undefined,
          taxId: data.taxId || undefined,
          workerFileKey:
            isParentSC && data.workerFileKey.length
              ? data.workerFileKey[0]
              : undefined,
        },
        location: {
          countryId: data.country || undefined,
          stateId: toNumber(data.state) || undefined,
          internationalStateName: data.internationalStateName || undefined,
          city: data.city || undefined,
          internationalCityName: data.internationalCityName || undefined,
          address: data.address || undefined,
          zipCode: data.zip || undefined,
        },
      },
    };
    const result = await dispatch(updateContractorCompany(values));

    if (updateContractorCompany.fulfilled.match(result)) {
      BaseAlert.success('Changes successfully saved.');
      onSuccess();
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleModalClose = () => {
    if (formRef?.current.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal title="Edit company" open={true} onCancel={handleModalClose}>
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={getSubmitValidationSchema()}
      >
        {(formik) => (
          <>
            <Form layout="vertical">
              <div className="modal-scroll-wrapper">
                <div className="modal-title">
                  {isParentSC
                    ? 'Parent subcontractor information'
                    : 'Contractor company information'}
                </div>

                <Row gutter={40}>
                  <Col xs={24} md={12}>
                    <FormItem
                      name="companyName"
                      label={
                        isParentSC
                          ? 'Parent SC company name *'
                          : 'Contractor company name *'
                      }
                    >
                      <Input
                        name="companyName"
                        placeholder={`Enter ${
                          isParentSC
                            ? 'parent SC company'
                            : 'contractor company'
                        } name`}
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      name="firstName"
                      label="Primary contact first name *"
                    >
                      <Input
                        name="firstName"
                        placeholder="Enter primary contact first name"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem
                      name="lastName"
                      label="Primary contact last name *"
                    >
                      <Input
                        name="lastName"
                        placeholder="Enter primary contact last name"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem name="email" label="Email *">
                      <Input
                        name="email"
                        placeholder="Enter email"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem label="Phone number *" name="phone">
                      <Input
                        name="phone"
                        placeholder="Enter phone number"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>

                  {companyDetails.contractorCompanyType ===
                    ContractorCompanyTypeEnum.ParentSC && (
                    <>
                      <Col xs={24} md={12}>
                        <FormItem label="Tax ID" name="taxId">
                          <InputNumber
                            name="taxId"
                            placeholder="Enter tax ID"
                            className="full-width"
                          />
                        </FormItem>
                      </Col>

                      <Col xs={24} md={12}>
                        <h5 className="modal-title">
                          Please use this section to upload worker's W9 Form
                        </h5>
                        <div className="upload-wrapper">
                          <FormItem
                            name="workerFileKey"
                            showValidateError={false}
                          >
                            <CompanyFileUpload
                              contractorCompanyType={
                                companyDetails.contractorCompanyType
                              }
                              fieldName="workerFileKey"
                              isAdmin={isAdmin as boolean}
                              contractorCompanyFiles={
                                contractorCompanyFileKeyInfo
                              }
                            />
                          </FormItem>
                        </div>
                      </Col>
                    </>
                  )}
                </Row>

                <div className="modal-title">Business address</div>

                {checkIsCountriesWithStatesDropdown(formik.values.country) ? (
                  <CompanyStateSection formik={formik} />
                ) : (
                  <InternationalCompanyStateSection formik={formik} />
                )}
              </div>

              <BaseModalFooter>
                <Button onClick={handleModalClose}>Cancel</Button>
                <SubmitButton type="primary">Save</SubmitButton>
              </BaseModalFooter>
            </Form>
          </>
        )}
      </Formik>
    </BaseModal>
  );
}
